import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Modal from "../../components/Modal";
import http from "../../api/apiClient";
import { formatDutchDate } from "../../utils/dutchDate";

type Visibility = { visible: boolean; updatedAt?: string | null };

/**
 * Whether parents see their parent contribution in the parent portal. Closed until the finance department has
 * processed the payments; opening it asks for confirmation because every parent then sees their open months.
 */
export default function ParentPortalVisibility({ canManage }: { canManage: boolean }): ReactElement | null {
  const { t } = useTranslation();
  const [state, setState] = useState<Visibility | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    http.get("/finance/parent-portal")
      .then(({ data }) => {
        if (!cancelled) setState({ visible: Boolean(data?.visible), updatedAt: data?.updatedAt ?? null });
      })
      .catch(() => {
        if (!cancelled) setState(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (visible: boolean) => {
    setBusy(true);
    setError(false);
    try {
      const { data } = await http.put("/finance/parent-portal", { visible });
      setState({ visible: Boolean(data?.visible), updatedAt: data?.updatedAt ?? null });
      setConfirming(false);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  return (
    <div
      data-parent-portal-visible={state.visible ? "true" : "false"}
      className={`finance-students-no-print flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-2.5 text-sm ${state.visible
        ? "border-emerald-200 bg-emerald-50/70 text-emerald-900 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-100"
        : "border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"}`}
    >
      {state.visible ? <FaEye aria-hidden="true" /> : <FaEyeSlash aria-hidden="true" />}
      <span className="min-w-0 flex-1">
        {state.visible
          ? t("finance.parent_portal.visible", {
            date: state.updatedAt ? formatDutchDate(state.updatedAt) : "",
            defaultValue: "Parents see their parent contribution in the parent portal.",
          })
          : t("finance.parent_portal.hidden", { defaultValue: "Parents do not see their parent contribution in the parent portal yet." })}
      </span>
      {error && (
        <span role="alert" className="text-xs font-semibold text-rose-600">
          {t("finance.parent_portal.save_failed", { defaultValue: "Changing this failed. Please try again." })}
        </span>
      )}
      {canManage && (
        <button
          type="button"
          disabled={busy}
          onClick={() => (state.visible ? save(false) : setConfirming(true))}
          className={`h-8 rounded-lg px-3 text-xs font-bold disabled:opacity-60 ${state.visible
            ? "border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 dark:border-emerald-400/40 dark:bg-transparent dark:text-emerald-100"
            : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
        >
          {state.visible
            ? t("finance.parent_portal.hide", { defaultValue: "Hide again" })
            : t("finance.parent_portal.open", { defaultValue: "Open for parents" })}
        </button>
      )}

      <Modal
        show={confirming}
        type="confirm"
        title={t("finance.parent_portal.confirm_title", { defaultValue: "Open for parents?" })}
        message={t("finance.parent_portal.confirm_message", {
          defaultValue: "Every parent will then see per child which months are paid and which are still open, and the payments received. Only do this once the payments have been processed.",
        })}
        onConfirm={() => save(true)}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
